import React, { useEffect, useState } from "react";

// Mirrors engine/schema.py's COLUMN_GROUPS -- a pure display-layout
// constant (which groups sit in which of the two columns), not business
// logic, so duplicating it here rather than importing Python into the
// JS bundle is the right call. Keep in sync with schema.py by hand if
// the group set ever changes.
const COLUMN_GROUPS = [
  ["CBC", "THYROID", "AUTOANTIBODIES"],
  ["AUTO_DIFFERENTIAL", "CHEMISTRY"],
];

const GROUP_LABELS = {
  CBC: "CBC",
  AUTO_DIFFERENTIAL: "AUTO DIFFERENTIAL",
  CHEMISTRY: "CHEMISTRY",
  THYROID: "THYROID",
  AUTOANTIBODIES: "AUTOANTIBODIES",
};

// Years/months granularity -- App.js's own formatAge() only goes to
// days, the wrong unit for a lab panel read years apart. No isStale()
// counterpart on purpose: Calibration never drives a same-day decision,
// so age is just the interval a trajectory is read across, not a
// staleness warning.
function formatPanelAge(isoDate) {
  if (!isoDate) return null;
  const then = new Date(isoDate + "T00:00:00");
  const now = new Date();
  let months = (now.getFullYear() - then.getFullYear()) * 12 + (now.getMonth() - then.getMonth());
  if (now.getDate() < then.getDate()) months -= 1;
  if (months < 1) {
    const days = Math.max(0, Math.round((now - then) / 86400000));
    return `${days}D OLD`;
  }
  if (months < 12) return `${months}MO OLD`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  return rem === 0 ? `${years}Y OLD` : `${years}Y ${rem}MO OLD`;
}

// Dot position within the bar: the reference range always occupies the
// middle 60% (20%-80%) of the track, value interpolated linearly within
// it and clamped so an out-of-range value still lands on the bar rather
// than off the end. Mirrors the original mockup's renderVals() exactly.
// Verified against real parsed data and an actual rendered page (not
// just the formula) -- e.g. Neutrophils (3 in 1.2-6.0, below the 3.6
// midpoint) renders at 42.5%, genuinely left of center; a 7.5-point
// offset on a ~370px bar is only ~28px, easy to misjudge next to RDW/
// MCHC's far more dramatic near-edge positions on the same screen.
//
// Only drawn when BOTH bounds are real numbers -- a two-sided range can
// be honestly interpolated. A one-sided range (">= 60", "< 50") has no
// second bound to interpolate against; see rangeTickInfo() below for
// how those render instead.
function rangeBarPct(value, lo, hi) {
  if (typeof value !== "number" || typeof lo !== "number" || typeof hi !== "number" || hi === lo) return null;
  const pct = 20 + ((value - lo) / (hi - lo)) * 60;
  return Math.max(3, Math.min(97, pct));
}

// For a one-sided range (exactly one of lo/hi is a real number), there
// is no second bound to scale a value against -- doing so would mean
// inventing one, which the mockup did (eGFR's real ">=60" got an
// invented 120 ceiling) and which this project's real-data-only
// discipline rules out. Instead: a fixed tick marks where the real
// bound sits, and the dot sits at a fixed, QUALITATIVE offset from it
// (compliant side vs. non-compliant side) -- this conveys direction
// honestly without implying a false numeric scale. Magnitude above/
// below the bound is never implied, only which side of it the value
// falls on (which the flag/color already corroborates).
function rangeTickInfo(value, lo, hi) {
  if (typeof value !== "number") return null;
  if (typeof lo === "number" && typeof hi !== "number") {
    // Lower-bound-only (">= lo" / "> lo") -- compliant means at or above it.
    return { tickPct: 30, dotPct: value >= lo ? 62 : 8 };
  }
  if (typeof hi === "number" && typeof lo !== "number") {
    // Upper-bound-only ("< hi" / "<= hi") -- compliant means at or below it.
    return { tickPct: 70, dotPct: value <= hi ? 38 : 92 };
  }
  return null;
}

function MarkerRow({ row }) {
  const color = row.flag ? "var(--accent-amber)" : "var(--accent-green)";
  const pct = rangeBarPct(row.value, row.range_low, row.range_high);
  const tick = pct === null ? rangeTickInfo(row.value, row.range_low, row.range_high) : null;
  return (
    <div className="marker-row">
      <div className="marker-label">{row.label}</div>
      <div className="marker-value" style={{ color }}>
        {typeof row.value === "number" ? row.value : row.value}
        {row.flag && <span className="marker-flag" style={{ color }}>{row.flag}</span>}
      </div>
      <div className="marker-range-bar">
        {pct !== null && (
          <>
            <div className="marker-range-bar-track" />
            <div className="marker-range-bar-zone" />
            <div className="marker-range-bar-dot" style={{ left: `${pct}%`, background: color }} />
          </>
        )}
        {tick !== null && (
          <>
            <div className="marker-range-bar-track" />
            <div className="marker-range-bar-tick" style={{ left: `${tick.tickPct}%` }} />
            <div className="marker-range-bar-dot" style={{ left: `${tick.dotPct}%`, background: color }} />
          </>
        )}
      </div>
      <div className="marker-range">{row.range_text || ""}</div>
    </div>
  );
}

function PanelCard({ panel }) {
  const allRows = Object.values(panel.groups || {}).flat();
  const flagged = allRows.filter(r => r.flag);
  const uncatalogued = panel.groups?.UNCATALOGUED || [];

  return (
    <div className="panel-card">
      <div className="panel-meta">
        <div className="panel-meta-row">
          <span className="panel-date">[{panel.collected_date || "UNKNOWN"}]</span>
          <span className="panel-age">{formatPanelAge(panel.collected_date)}</span>
          {uncatalogued.length > 0 && (
            <span className="panel-uncatalogued-flag">
              {uncatalogued.length} UNCATALOGUED
            </span>
          )}
        </div>
        <div className="panel-source-row">
          <span>{panel.lab}{panel.location ? ` · ${panel.location}` : ""}{panel.lab_number ? ` · ${panel.lab_number}` : ""}</span>
          <span className="panel-source-badge">● {panel.source_system}</span>
        </div>
        {flagged.length > 0 && (
          <div className="panel-lab-flags">
            LAB FLAGS: {flagged.map(r => `${r.label} ${r.flag}`).join(" · ")}
          </div>
        )}
      </div>

      <div className="panel-columns">
        {COLUMN_GROUPS.map((groupNames, colIdx) => (
          <div className="panel-column" key={colIdx}>
            {groupNames.map(groupName => {
              const rows = panel.groups?.[groupName];
              if (!rows || rows.length === 0) return null;
              return (
                <div key={groupName}>
                  <div className="marker-group-name">{GROUP_LABELS[groupName] || groupName}</div>
                  {rows.map((row, i) => <MarkerRow row={row} key={i} />)}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {uncatalogued.length > 0 && (
        <div className="panel-column" style={{ borderTop: "1px solid var(--line-bright)" }}>
          <div className="marker-group-name" style={{ color: "var(--accent-red)" }}>
            UNCATALOGUED — not yet in engine/schema.py
          </div>
          {uncatalogued.map((row, i) => <MarkerRow row={row} key={i} />)}
        </div>
      )}

      <div className="panel-trend-footer">TREND RENDERS AT 2+ PANELS</div>
    </div>
  );
}

export default function CalibrationPage({ authedFetch }) {
  const [panels, setPanels] = useState(null); // null = loading
  const [error, setError] = useState(null);

  useEffect(() => {
    authedFetch("/api/calibration")
      .then(res => res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`)))
      .then(data => setPanels(Array.isArray(data) ? data : []))
      .catch(err => { setError(err.message); setPanels([]); });
  }, [authedFetch]);

  const sortedPanels = (panels || [])
    .slice()
    .sort((a, b) => (b.collected_date || "").localeCompare(a.collected_date || ""));

  return (
    <div className="calibration-page">
      <div className="calibration-subheader">
        <div>
          <div className="calibration-subheader-label">CALIBRATION // SLOW DATA</div>
          <div className="calibration-tagline">CONSTRAINS. NEVER COMMANDS.</div>
        </div>
        <button className="btn-add-panel" disabled title="Ingest a new panel via pipeline/tools/calibration_bridge.py">
          [ + ADD PANEL ]
        </button>
      </div>

      <div className="calibration-panel-count">
        MEASURED // {sortedPanels.length} PANEL{sortedPanels.length === 1 ? "" : "S"}
      </div>

      {panels === null ? (
        <div className="calibration-empty">LOADING...</div>
      ) : error ? (
        <div className="calibration-empty">ERROR // {error}</div>
      ) : sortedPanels.length === 0 ? (
        <div className="calibration-empty">AWAITING DATA</div>
      ) : (
        sortedPanels.map(panel => <PanelCard panel={panel} key={panel.lab_number} />)
      )}

      <div className="genome-section-label">FIXED // GENOME</div>
      <div className="genome-box">
        <div>
          <div className="genome-label">GENOME</div>
          <div><span className="genome-value">--</span><span className="genome-unit">VARIANTS</span></div>
          <div className="genome-status">AWAITING DATA · LOCAL-ONLY · ENCRYPTED AT REST · NEVER AGES</div>
        </div>
        <button className="btn-import-genome" disabled title="A genome test is ordered but not yet imported">
          [ IMPORT GENOME FILE ]
        </button>
      </div>
    </div>
  );
}
